import React from "react";
import { graphql, Link } from "gatsby";
import {
  Breadcrumb,
  Container,
  Header,
  Icon,
  Image,
  Item,
  List,
  Label,
  Message,
} from "semantic-ui-react";

import Layout from "../components/layout";
import SEO from "../components/seo";
import Observation from "../components/Observation";

import logo from "../images/progmiscon-logo-64.png";

function Authors({ authors }) {
  return (
    <List horizontal>
      {authors.map(author => (
        <List.Item key={`${author.firstName}-${author.lastName}`}>
          <span style={{ fontSize: "120%" }}>
            {author.firstName} <strong>{author.lastName}</strong>
          </span>
        </List.Item>
      ))}
    </List>
  );
}

function Abstract({ abstract }) {
  return abstract ? <div>{abstract}</div> : null;
}

function TypeUnknown({ abstract }) {
  return (
    <>
      <Item>
        <Item.Content>
          <Item.Description>
            <Abstract abstract={abstract} />
          </Item.Description>
        </Item.Content>
      </Item>
    </>
  );
}

function ConferencePaper({ abstract, proceedingsTitle }) {
  return (
    <>
      <Item>
        <Item.Content>
          <Item.Description>
            <p>
              <strong>{proceedingsTitle}</strong>
            </p>
            <Abstract abstract={abstract} />
          </Item.Description>
        </Item.Content>
      </Item>
    </>
  );
}

function Thesis({ abstract, thesisType, university, place }) {
  return (
    <>
      <Item>
        <Item.Content>
          <Item.Description>
            <p>
              <strong>
                {thesisType} - {university} - {place}
              </strong>
            </p>
            <Abstract abstract={abstract} />
          </Item.Description>
        </Item.Content>
      </Item>
    </>
  );
}

function JournalArticle({ abstract, publication, volume, issue, pages }) {
  return (
    <>
      <Item>
        <Item.Content>
          <Item.Description>
            <p>
              <strong>
                {publication}, Volume {volume}, Issue {issue}{" "}
                {pages ? `, ${pages}` : null}
              </strong>
            </p>
            <Abstract abstract={abstract} />
          </Item.Description>
        </Item.Content>
      </Item>
    </>
  );
}

function Report({ abstract, reportType, reportNumber, seriesTitle }) {
  return (
    <>
      <Item>
        <Item.Content>
          <Item.Description>
            <p>
              <strong>
                {reportType || "Report"}, {reportNumber}, {seriesTitle}
              </strong>
            </p>
            <Abstract abstract={abstract} />
          </Item.Description>
        </Item.Content>
      </Item>
    </>
  );
}

function WebPage({ url, abstract, websiteTitle, accessed }) {
  return (
    <>
      <Item>
        <Item.Content>
          <Item.Description>
            <p>
              <a href={url} target='_blank' rel='noreferrer'>
                {websiteTitle}
              </a>{" "}
              - Last accessed: {accessed}
            </p>
            <Abstract abstract={abstract} />
          </Item.Description>
        </Item.Content>
      </Item>
    </>
  );
}

function BookSection({ abstract, bookTitle, publisher, pages }) {
  return (
    <>
      <Item>
        <Item.Content>
          <Item.Description>
            <p>
              <strong>
                {bookTitle}, {publisher}, {pages}
              </strong>
            </p>
            <Abstract abstract={abstract} />
          </Item.Description>
        </Item.Content>
      </Item>
    </>
  );
}

const getReferenceComponent = reference => {
  if (!reference || !reference.data) {
    return <>Reference not found</>;
  }

  const { itemType } = reference.data;

  switch (itemType) {
    case "bookSection":
      return (
        <BookSection
          abstract={reference.data.abstractNote}
          bookTitle={reference.data.bookTitle}
          publisher={reference.data.publisher}
          pages={reference.data.pages}
        />
      );
    case "webpage":
      return (
        <WebPage
          url={reference.data.url}
          abstract={reference.data.abstractNote}
          websiteTitle={reference.data.websiteTitle}
          accessed={reference.data.accessDate}
        />
      );
    case "report":
      return (
        <Report
          abstract={reference.data.abstractNote}
          reportType={reference.data.reportType}
          reportNumber={reference.data.reportNumber}
          seriesTitle={reference.data.seriesTitle}
        />
      );
    case "thesis":
      return (
        <Thesis
          abstract={reference.data.abstractNote}
          thesisType={reference.data.thesisType}
          university={reference.data.university}
          place={reference.data.place}
        />
      );
    case "journalArticle":
      return (
        <JournalArticle
          abstract={reference.data.abstractNote}
          publication={reference.data.publicationTitle}
          volume={reference.data.volume}
          issue={reference.data.issue}
          pages={reference.data.numPages}
        />
      );
    case "conferencePaper":
      return (
        <ConferencePaper
          abstract={reference.data.abstractNote}
          proceedingsTitle={reference.data.proceedingsTitle}
        />
      );
    default:
      return <TypeUnknown abstract={reference.data.abstractNote} />;
  }
};

const ReferencePageTemplate = ({ data }) => {
  const reference = data.zoteroItems;
  const referenceCount = data.misconceptions.edges
    ? data.misconceptions.edges.length
    : 0;

  const refEntry = data.refs.edges.filter(
    edge => edge.node.slug === "references/" + reference.citationKey
  );

  const allMisconceptionsOfThisReference = [
    ...new Set(
      refEntry.flatMap(
        e =>
          e.node.frontmatter?.phenomenonKinds?.flatMap(
            pk => pk?.phenomena?.flatMap(p => p.plMisconceptions || []) || []
          ) || []
      )
    ),
  ];

  const relatedMisconceptions = data.misconceptions.edges
    .map(e => e.node)
    .filter(n =>
      allMisconceptionsOfThisReference.some(
        mc => mc.name === n.fields.name && mc.lang === n.fields.pl
      )
    );

  return (
    <Layout>
      <SEO title={reference.citationKey} />
      <Container text style={{ marginTop: "100px", marginBottom: "100px" }}>
        <Breadcrumb>
          <Breadcrumb.Section link as={Link} to='/'>
            Home
          </Breadcrumb.Section>
          <Breadcrumb.Divider icon='right chevron' />
          <Breadcrumb.Section link as={Link} to='/references'>
            References
          </Breadcrumb.Section>
          <Breadcrumb.Divider icon='right chevron' />
          <Breadcrumb.Section active>
            {reference.citationKey}
          </Breadcrumb.Section>
        </Breadcrumb>

        <Header as='h1' dividing>
          <Header.Content>{reference.data.title}</Header.Content>
          <Header.Subheader>
            <Authors authors={reference.data.creators} />
          </Header.Subheader>
        </Header>

        <p>{reference.data.parsedDate.substring(0, 4)}</p>

        {getReferenceComponent(reference)}

        {reference.data.url ? (
          <Message icon info>
            <Icon name='file alternate outline' />
            <Message.Content>
              <Message.Header>Read the Paper</Message.Header>
              <a href={reference.data.url} target='_blank' rel='noreferrer'>
                {reference.data.url}
              </a>
            </Message.Content>
          </Message>
        ) : null}

        {refEntry.map(edge => (
          <div key={edge.node.slug}>
            <Header as='h2' dividing>
              <Header.Content>Study Information</Header.Content>
              <Header.Subheader>
                Manually extracted from the paper by the Progmiscon.org team
              </Header.Subheader>
            </Header>

            <Header as='h3'>
              <Header.Content>Programming Languages</Header.Content>
            </Header>

            <div>
              {edge.node.frontmatter.langs.map(lang => (
                <Label key={lang} color='red'>
                  {lang}
                </Label>
              ))}
            </div>

            <Header as='h3'>
              <Header.Content>Method</Header.Content>
            </Header>
            <p>{edge.node.frontmatter.method}</p>

            <Header as='h3'>
              <Header.Content>Subjects</Header.Content>
            </Header>
            <p>{edge.node.frontmatter.subjects}</p>

            <Header as='h3'>
              <Header.Content>Artifact</Header.Content>
            </Header>
            {edge.node.frontmatter.artifact ? (
              <>
                {edge.node.frontmatter.artifact.url ? (
                  <a href={edge.node.frontmatter.artifact.url}>
                    {edge.node.frontmatter.artifact.url}
                  </a>
                ) : null}
                {edge.node.frontmatter.artifact.note ? (
                  <Message icon warning>
                    <Icon name='sticky note outline' />
                    <Message.Content>
                      <Message.Header>
                        Note by Progmiscon.org Team
                      </Message.Header>
                      {edge.node.frontmatter.artifact.note}
                    </Message.Content>
                  </Message>
                ) : null}
              </>
            ) : (
              <Message icon warning>
                <Icon name='sticky note outline' />
                <Message.Content>
                  <Message.Header>Note by Progmiscon.org Team</Message.Header>
                  We are not aware of an artifact supporting this paper.
                </Message.Content>
              </Message>
            )}

            <Header as='h2' dividing>
              <Header.Content>Related Study Results</Header.Content>
              <Header.Subheader>
                Phenomena studied in this paper that map to Progmiscon.org
                misconceptions
              </Header.Subheader>
            </Header>

            {edge.node.frontmatter.phenomenonKinds ? (
              <>
                <p>
                  The following list summarizes those phenomena reported in this
                  study that provide evidence for misconceptions documented on
                  Progmiscon.org. (The paper may provide evidence for other
                  misconceptions as well. This list focuses exclusively on
                  misconceptions documented on Progmiscon.org.)
                </p>
                {edge.node.frontmatter.phenomenonKinds.map(phenomenonKind => (
                  <div key={phenomenonKind.name}>
                    <Header as='h3'>
                      <Header.Content>{phenomenonKind.name}</Header.Content>
                      <Header.Subheader>
                        {phenomenonKind.description}
                      </Header.Subheader>
                    </Header>
                    <List selection>
                      {phenomenonKind.phenomena.map(phenomenon => (
                        <List.Item key={phenomenon.id}>
                          <List.Content>
                            <List.Header>{phenomenon.id}</List.Header>
                            <List.Description>
                              {phenomenon.name}
                            </List.Description>
                            <List.Description>
                              {phenomenon.observations
                                ? phenomenon.observations.map(o => (
                                    <Observation key={o.measure} o={o} />
                                  ))
                                : null}
                            </List.Description>
                            <List.Description>
                              <div>
                                This provides evidence potentially relevant for
                                the following Progmiscon.org misconceptions:
                              </div>
                              <List selection>
                                {phenomenon.plMisconceptions?.map(mc => (
                                  <List.Item
                                    as='a'
                                    href={`/misconceptions/${mc.lang}/${mc.name}`}
                                    key={`${mc.lang}/${mc.name}`}
                                  >
                                    <Image
                                      alt='Misconception'
                                      width='20'
                                      src={logo}
                                    />
                                    <List.Content
                                      style={{
                                        width: "calc(100% - 20px)",
                                      }}
                                    >
                                      <List.Header>
                                        <span style={{ color: "#4183c4" }}>
                                          {mc.name}
                                        </span>
                                        <span> &mdash; {mc.lang}</span>
                                      </List.Header>
                                    </List.Content>
                                  </List.Item>
                                ))}
                              </List>
                            </List.Description>
                            <List.Description>
                              {phenomenon.note ? (
                                <Message icon warning>
                                  <Icon name='sticky note outline' />
                                  <Message.Content>
                                    <Message.Header>
                                      Note by Progmiscon.org Team
                                    </Message.Header>
                                    {phenomenon.note}
                                  </Message.Content>
                                </Message>
                              ) : null}
                            </List.Description>
                          </List.Content>
                        </List.Item>
                      ))}
                    </List>
                  </div>
                ))}
              </>
            ) : (
              <p>No referenced misconceptions for this publication.</p>
            )}
          </div>
        ))}
      </Container>
    </Layout>
  );
};

export default ReferencePageTemplate;

export const query = graphql`
  query ($citationKey: String!) {
    zoteroItems(citationKey: { eq: $citationKey }) {
      citationKey
      data {
        key
        version
        itemType
        title
        abstractNote
        date
        proceedingsTitle
        conferenceName
        thesisType
        university
        place
        publisher
        volume
        pages
        series
        language
        DOI
        ISBN
        shortTitle
        url
        accessDate(formatString: "YYYY.MM.DD")
        archive
        archiveLocation
        libraryCatalog
        callNumber
        rights
        extra
        dateAdded
        dateModified
        reportNumber
        reportType
        seriesTitle
        institution
        publicationTitle
        issue
        seriesText
        journalAbbreviation
        ISSN
        bookTitle
        seriesNumber
        numberOfVolumes
        edition
        creators {
          creatorType
          firstName
          lastName
        }
        tags {
          tag
          type
        }
        parsedDate
        creatorSummary
      }
    }
    misconceptions: allMdx(
      filter: {
        fileAbsolutePath: { glob: "**/src/data/misconceptions/**/index.md" }
      }
      sort: { fields: slug, order: ASC }
    ) {
      totalCount
      edges {
        node {
          slug
          fields {
            slug
            name
            pl
          }
          frontmatter {
            shortDescription
          }
        }
      }
    }
    refs: allMdx(filter: { frontmatter: { type: { eq: "ref" } } }) {
      edges {
        node {
          id
          slug
          frontmatter {
            langs
            method
            subjects
            artifact {
              url
              note
            }
            phenomenonKinds {
              name
              description
              phenomena {
                id
                name
                observations {
                  kind
                  measure
                  count
                  maxCount
                  percent
                  counts
                  levelLabels
                  value
                  maxValue
                  maxLabel
                  minValue
                  minLabel
                  category
                  categoryDescription
                }
                plMisconceptions {
                  lang
                  name
                }
                note
              }
            }
          }
          fields {
            slug
            citationKey
          }
        }
      }
    }
  }
`;
